import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux'
import { useParams, useNavigate } from 'react-router-dom'

import NoAccess from '../components/psc/lib/NoAccess'
import StatusOptionsMulti from '../components/psc/lib/StatusOptionsMulti'
import SearchBox from '../components/psc/lib/SearchBox'
import Checkbox from '../components/psc/lib/Checkbox'
import CancelButton from '../components/psc/lib/CancelButton'
import SaveButton from '../components/psc/lib/SaveButton'
import ErrorMessage from '../components/psc/lib/ErrorMessage'
import GncDataTable from '../components/psc/lib/GncDataTable'
import Loading from '../components/psc/lib/Loading'

import useAuthentication from '../hooks/useAuthentication';
import useUserManagement from '../hooks/useUserManagement';
import useTableSlots from '../hooks/useTableSlots';
import useBrowserTabTitle from '../hooks/useBrowserTabTitle';

const DeliveryListDocuments = () => {
    const navigate = useNavigate()
    const params = useParams();
    const projectID = Number(params.projectID);
    const objID = Number(params.objID);
    
    const pscState = useSelector(state => state.pscState.value)
    const project = pscState.project

    const table = useRef(null)
    const [tableData, setTableData] = useState([])
    const localStorageOrderKey = 'tblDeliveryListDocumentsOrder'
    const defaultOrder = [[ 1, 'asc' ]]

    const { currentUser } = useAuthentication();
    const { setBrowserTabTitle } = useBrowserTabTitle()

    const { userHasProjectAccessLevel } = useUserManagement();

    const {
        dateSlot,
        filePathSlot,
        linkSlot,
        ownerSlot
    } = useTableSlots()

    const [allDocuments, setAllDocuments] = useState([])
    const [allIncludedDocuments, setAllIncludedDocuments] = useState([])

    const [allDocumentsLoaded, setAllDocumentsLoaded] = useState(false)
    const [allIncludedDocumentsLoaded, setAllIncludedDocumentsLoaded] = useState(false)
    
    const [listDocuments, setListDocuments] = useState([])
    const [listIncludedDocuments, setListIncludedDocuments] = useState([])

    // Form controls
    const [error, setError] = useState([])
    const [submitActive, setSubmitActive] = useState(false)
    const [submitSpinner, setSubmitSpinner] = useState(false)
    const [updatedAt, setUpdatedAt] = useState(null)
    const [hasAccess, setHasAccess] = useState(true)

    const [title, setTitle] = useState('')

    useEffect(() => {
        // Wait for all to be loaded
        if (!allDocumentsLoaded && !allIncludedDocumentsLoaded) {
            return
        }
        
        let data = []
        data.push(...allDocuments.map(item => {
            return {
                is_included: listDocuments.find(function (d) { return d.id === item.id }) ? true : false,
                type: 'documents',
                id: item.id,
                project_id: item.project_id,
                title: item.title,
                reference: item.reference,
                final_version_file_path: item.final_version_file_path,
                std_document_name: item.std_document.name,
                document_status_name: item.document_status.name,
            }
        }))
        data.push(...allIncludedDocuments.map(item => {
            return {
                is_included: listIncludedDocuments.find(function (d) { return d.id === item.id }) ? true : false,
                type: 'included_documents',
                id: item.id,
                project_id: item.project_id,
                title: item.original.title,
                reference: item.original.reference,
                final_version_file_path: item.original.final_version_file_path,
                std_document_name: item.std_document.name,
                document_status_name: item.original.document_status.name,
            }
        }))
        setTableData(data)

    }, [allDocuments, allIncludedDocuments, allDocumentsLoaded, allIncludedDocumentsLoaded])

    useEffect(() => {
        fetch('/v0/documents?project_id=' + projectID, { credentials: 'same-origin' })
            .then(function(response) {
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }
                return response.json();
            })
            .then(function(json) {
                setAllDocumentsLoaded(true)
                setAllDocuments(json.results);
            })
            .catch(function(error) {
                console.log('Error:', error);
                setHasAccess(false);
            });

        fetch('/v0/included_documents?project_id=' + projectID, { credentials: 'same-origin' })
            .then(function(response) {
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }
                return response.json();
            })
            .then(function(json) {
                setAllIncludedDocumentsLoaded(true)
                setAllIncludedDocuments(json.results || []);
            })
            .catch(function(error) {
                console.log('Error:', error);
                setHasAccess(false);
            });
    }, [])


    useEffect(() => {
        fetch('/v0/delivery_lists/' + objID, { credentials: 'same-origin' })
            .then(function(response) {
                if (!response.ok) {
                    if (response.status === 403 || response.status === 404) {
                        setHasAccess(false);
                    }
                    throw new Error('Network response was not ok');
                }
                return response.json();
            })
            .then(function(json) {
                if (json.error) {
                    throw new Error(json.error);
                }
                setTitle(json.result.title);
                setListDocuments(json.result.list_documents);
                setListIncludedDocuments(json.result.list_included_documents);
                setUpdatedAt(json.result.updated_at);
            })
            .catch(function(error) {
                console.log('Error fetching delivery list:', error);
                setHasAccess(false);
            })
    }, [])

    useEffect(() => {
        if( title ) {
            setBrowserTabTitle(`${title} Documents`)
        }
    }, [title, setBrowserTabTitle])
    
    const columns = [
        { data: 'is_included' },
        { data: 'final_version_file_path' },
        { data: 'reference' },
        { data: 'title' },
        { data: 'std_document_name' },
        { data: 'document_status_name' }
    ]

    const tableHead = (
        <tr>
            <th style={{width: '25px'}}></th>
            <th style={{width: '25px'}}>&nbsp;</th>
            <th>Reference</th>
            <th>Title</th>
            <th>Document</th>
            <th>Status</th>
        </tr>
    )

    // Straight edit datatable data
    const toggleRoleCheckbox = (item) => {
        const type = item.type
        const id = item.id
        const isIncluded = item.is_included
        table.current.dt().rows().every( (index) => {
            const row = table.current.dt().row(index)
            const data = row.data()
            if( type === data.type && data.id === id ) {
                data.is_included = !isIncluded
                row.data(data)
            }
            return true
        })
    }

    const tableSlots = {
        0: (data, type, row) => {
            if( type !== 'display') {
                return data
            }
            return (
                <Checkbox
                    onChange={() => toggleRoleCheckbox(row)}
                    isChecked={data}
                />
            )
        },
        1: filePathSlot,
        2: (data, type, row) => linkSlot(data, type, row, `/portfolio/${row.project_id}/${row.type}/${row.id}`),
        3: (data, type, row) => linkSlot(data, type, row, `/portfolio/${row.project_id}/${row.type}/${row.id}`),
        4: (data, type, row) => {
            if( type !== 'display') {
                return data
            }
            return data
        },
    }
    
    const handleSubmitError = (props) => {
        setSubmitActive(false)
        setSubmitSpinner(false)
        setError([ props.message ])
    }

    const toBackend = () => {
        let list_documents = []
        let list_included_documents = []
        table.current.dt().rows().every( (index) => {
            const row = table.current.dt().row(index)
            const data = row.data()
            if ( data.is_included ) {
                let obj = {
                    id: data.id,
                    type: data.type,
                }
                if( data.type === 'documents' ) {
                    list_documents.push(obj)
                } else {
                    list_included_documents.push(obj)
                }
            }
            return true
        })
        return {
            updated_at: updatedAt,
            list_documents: list_documents,
            list_included_documents: list_included_documents,
        }
    }

    const handleSubmit = (event) => {
        if( event ) {
            event.preventDefault()
        }
        let submit = {
            objType: 'delivery_lists',
            objID: objID,
            data: toBackend(),
            navigateOverride: () => {
                navigate('/portfolio/'+projectID+'/delivery_lists/'+objID)
            }
        }
        return submit
    }

    const project_type_name = project?.project_type?.name || 'Project'
    
    if (!hasAccess) {
        return <NoAccess />
    }

    if (!allDocumentsLoaded || !allIncludedDocumentsLoaded) {
        return <Loading />
    }
    
    return (
        <div className="wrapper wrapper-content">
            <div className="row">
                <div className="col-lg-12">
                    <div className="ibox float-e-margins">

                        <div className="ibox-title">
                            <h2>Edit Document Delivery List</h2>
                        </div>

                        <div className="ibox-content">
                            <form className="form-horizontal" onSubmit={(e) => handleSubmit(e)}>
                                <div className="row">
                                    <SearchBox tableRef={table} />
                                    <StatusOptionsMulti
                                        tableRef={table}
                                        statusColumnIndex="5"
                                        fetchUrl="/v0/document_statuses"
                                        localStorageKey="delivery-list-document-list-statuses"
                                    />
                                </div>

                                <div className="row">
                                    <GncDataTable
                                        tableRef={table}
                                        data={tableData}
                                        defaultOrder={defaultOrder}
                                        localStorageOrderKey={localStorageOrderKey}
                                        columns={columns}
                                        tableSlots={tableSlots}
                                        tableHead={tableHead}
                                        tableFoot={tableHead}
                                    />
                                </div> 

                                <div className="hr-line-dashed"></div>

                                <div className="form-group">
                                    <ErrorMessage
                                        error={error}
                                    />

                                    <div className="col-sm-8 col-sm-offset-2">
                                        <CancelButton />

                                        <SaveButton
                                            buttonVariant='primary'
                                            visible={userHasProjectAccessLevel(currentUser,project.id, "MEMBER")}
                                            spinnerActive={submitSpinner}
                                            label='Save Changes'
                                            onClick={handleSubmit}
                                            onError={handleSubmitError}
                                        />
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default DeliveryListDocuments
